import {
  ZOOM_SCALE_MAX,
  ZOOM_SCALE_MIN,
  ZOOM_SCALE_MIN_FACTOR,
} from '~/client/src/shared/map/mapZoomRule'
import { COLLISION_PADDING } from '~/client/src/shared/map/markerGeometry'

import Colors from '~/client/src/shared/theme.module.scss'

// Literal hex only — GL paint can't parse the CSS-var-based neutral tokens
// (hsla(…,var(--toneX),…)).

// A label picks its ink from the surface it sits on, per feature — the tone arrives as the
// feature's `tone`, decided by useLabelGroundTones.
//
// It reads the SCENE, never the rendered picture. Sampling the canvas made labels blink on a
// still map: the box was centred on its own glyphs, so a label's colour steered its next one.
export type BasemapInk = 'light' | 'dark'

// The tone a label falls back to before its first reading, and wherever a reading can't be
// taken at all (anchor off-screen, canvas read refused). The active basemap is the best
// available guess: satellite is dark ground far more often than not, street the reverse.
export const basemapInkFor = (isSatellite: boolean): BasemapInk =>
  isSatellite ? 'dark' : 'light'

const toneMatch = (onLight: string, onDark: string) => [
  'match',
  ['get', 'tone'],
  'light',
  onLight,
  onDark,
]

// Compared against `true`, never used bare: `['get', …]` is typed `value` and `any` rejects a
// non-boolean, so on a tier whose features carry neither property GL falls back to the
// property's default — transparent, for text-halo-color, which silently drops the casing.
const IS = (property: string) => ['==', ['get', property], true]

// Feature STATE, not the feature: state binds by id, so a source without promoteId reads null
// and this is permanently false. `hovered` lives here because it changes per pointer sample and
// a data property can only change by re-uploading the whole collection; `selected` stays a
// property because it also drives symbol-sort-key, and layout cannot read state at all.
const IS_STATE = (property: string) => ['==', ['feature-state', property], true]
const EMPHASISED = ['any', IS('selected'), IS_STATE('hovered')]

const emphasisFirst = (resting: unknown) =>
  ['case', EMPHASISED, Colors.mapLabelSelected, resting] as never

// A plated label's ground is the plate, and the plate is a known constant — so its ink is
// too. Selection still wins: blue wants a light casing, and the plate is one.
export const pinLabelInkPaint = () =>
  [
    'case',
    EMPHASISED,
    Colors.mapLabelSelected,
    IS('plated'),
    Colors.mapLabelOnLight,
    toneMatch(Colors.mapLabelOnLight, Colors.mapLabelOnDark),
  ] as never

// Shares the pin tier's pair. No plated branch, unlike that tier: a plated LOCATION label keeps
// the location's own colour, so its layer carries its own paint.
export const locationLabelInkPaint = () =>
  toneMatch(Colors.mapLabelOnLight, Colors.mapLabelOnDark) as never

// A plated LOCATION label's ink depends on why it is plated. Asked for in the editor, the plate
// is the user's own frame and their colour belongs on it. Forced by a plan sheet, the plate is a
// legibility rescue, so it takes the same constant the workflow tier's plate does — forcing a
// pale location colour onto white paper would trade one unreadable label for another.
export const locationPlateInkPaint = () =>
  [
    'case',
    IS('platedByPlan'),
    Colors.mapLabelOnLight,
    ['get', 'labelColor'],
  ] as never

export const markupLabelInkPaint = () =>
  emphasisFirst(
    toneMatch(Colors.mapLabelMutedOnLight, Colors.mapLabelMutedOnDark),
  )

// What a casing is set to when a plate has replaced it. Transparent rather than merely
// zero-width: a zero-width halo in an opaque colour still tints the antialiased edge of every
// glyph. Named because two callers need it — the per-feature case below, and a layer whose
// every feature is plated and takes the constant directly.
export const SUPPRESSED_HALO_COLOR = 'rgba(0, 0, 0, 0)'

export const labelHaloPaint = () =>
  [
    'case',
    EMPHASISED,
    Colors.mapHaloOnLight,
    IS('plated'),
    SUPPRESSED_HALO_COLOR,
    toneMatch(Colors.mapHaloOnLight, Colors.mapHaloOnDark),
  ] as never

// The casing's full-strength CORE is what separates the glyph, and it grows inward from
// every outline — including the inside of an 'e' — so `opening = counter - 2 * core`, with
// core = width - blur. At 1.5/0 the core was 1.50 against a 0.71 counter limit: negative
// opening, which is literally why SH-556P rendered as SH=556P. 0.8 of blur drops the core to
// 0.70 and turns the outer edge into a ramp instead of a cliff.
const LABEL_HALO_WIDTH_PX = 1.5
const LABEL_HALO_BLUR_PX = 0.8

// GL measures tracking in ems, so the design system's +1% is 0.01. It briefly ran at +6% on
// the pin tier to fight letters that read glued together — but that was the closed counters
// above, not tracking, and blur fixes the cause.
export const LETTER_SPACING_EM = 0.01

// Mapbox has no synthetic weights, so the weight has to come from a real face. Inter
// SemiBold is not in Mapbox's shared library — it is uploaded to our own accounts, and
// mapGlyphRewrite redirects this one fontstack's glyph requests there.
export const SEMIBOLD_LABEL_FONT = [
  'Inter SemiBold',
  'Arial Unicode MS Regular',
]
const MARKUP_LABEL_FONT = ['Inter Medium', 'Arial Unicode MS Regular']

export interface ILabelTier {
  sizePx: number
  lineHeightPx: number
  letterSpacingEm: number
  // The MAPBOX fontstack — server-side SDF glyph names. Not usable as a CSS family: see
  // labelMeasureFont.
  font: string[]
  // The same weight as `font`, in the form a canvas understands. Carried beside the fontstack
  // rather than parsed out of it, so the two cannot drift.
  weightCss: number
  haloWidthPx: number
  haloBlurPx: number
}

// The identity tier: workflow pin labels, the dominant read on the map.
export const PIN_LABEL: ILabelTier = {
  sizePx: 13,
  // 1.15 leading, the tightest in the map namespace and deliberate: two short lines naming
  // one object should read as one object, and there is no long measure to track back across.
  lineHeightPx: 15,
  letterSpacingEm: LETTER_SPACING_EM,
  font: SEMIBOLD_LABEL_FONT,
  weightCss: 600,
  haloWidthPx: LABEL_HALO_WIDTH_PX,
  haloBlurPx: LABEL_HALO_BLUR_PX,
}

// The annotation tier: markup labels. Quieter than PIN_LABEL through ink, never
// through opacity, which would dim the casing along with the glyph.
export const MARKUP_LABEL: ILabelTier = {
  sizePx: 12,
  lineHeightPx: 14,
  letterSpacingEm: LETTER_SPACING_EM,
  font: MARKUP_LABEL_FONT,
  weightCss: 500,
  haloWidthPx: LABEL_HALO_WIDTH_PX,
  haloBlurPx: LABEL_HALO_BLUR_PX,
}

// The place tier: location names. Same voice as the pin tier — a location's name and a
// workflow's name are the same kind of read — but its SIZE is the user's, set per item in the
// editor from 4px to 100px and then scaled again by the zoom ramp. sizePx here is only the
// nominal the tier's fixed pixel values are quoted against, which is what lets the casing
// scale with whatever size an item actually carries; the layer overrides text-size outright.
export const LOCATION_LABEL: ILabelTier = {
  sizePx: 12,
  lineHeightPx: 14,
  letterSpacingEm: LETTER_SPACING_EM,
  font: SEMIBOLD_LABEL_FONT,
  weightCss: 600,
  haloWidthPx: LABEL_HALO_WIDTH_PX,
  haloBlurPx: LABEL_HALO_BLUR_PX,
}

// The CSS font a canvas needs to measure what mapbox will lay out.
//
// A tier's `font` is a mapbox FONTSTACK — server-side SDF glyph sets — and naming one to a
// canvas does not fail, it silently falls through to the platform sans-serif. The error is not
// one-sided either: a wrong face can under- OR over-measure, and it moves with the OS.
export const MEASURE_FAMILY = '"Inter Variable", sans-serif'

export const labelMeasureFont = (
  tier: ILabelTier,
  sizePx: number = tier.sizePx,
): string => `${tier.weightCss} ${sizePx}px ${MEASURE_FAMILY}`

// One gap, measured one way, for every marker-and-label pair on the map. It had been four
// answers from three reference points, and the location one was not even constant — it fell
// out of Inter's leading, so the visible air changed with the label's size.
//
// The rule now: from the marker's PAINTED edge to the label's PAINTED edge — the plate's outer
// edge when plated, the casing's outer edge when not.
export const LABEL_MARKER_GAP_PX = 6

// Inter's metrics, and the shaping rule GL applies: the baseline sits at
// lineHeight/2 + (ascender - descender)/2 below the line box top (shapeLines/justifyLine).
export const INTER_ASCENDER_EM = 0.97
export const INTER_DESCENDER_EM = 0.24
const INTER_CAP_HEIGHT_EM = 0.7275

// How far the first line's cap top sits BELOW the line box top, in ems. text-offset positions
// the line box, not the ink, and the difference is leading that grows with the type — which is
// exactly what made a single lift constant give a different gap at every font size.
export const capTopOffsetEm = (tier: ILabelTier): number =>
  tier.lineHeightPx / tier.sizePx / 2 +
  (INTER_ASCENDER_EM - INTER_DESCENDER_EM) / 2 -
  INTER_CAP_HEIGHT_EM

// A ratio, not pixels: the location tier's size is the editor's to set, and the proportions are
// what keep a 4px label's counters as open as a 100px one's.
export const locationHaloWidthPx = (labelSize: number): number =>
  (LOCATION_LABEL.haloWidthPx / LOCATION_LABEL.sizePx) * labelSize

// Where an unplated label's line box goes so its CASING clears the marker's painted edge by
// LABEL_MARKER_GAP_PX. Both corrections scale with the label — leading pushes the ink down as
// type grows, casing pushes it back — so the visible gap holds at any size.
export const bareLabelLiftPx = (
  tier: ILabelTier,
  sizePx: number,
  haloWidthPx: number,
): number => LABEL_MARKER_GAP_PX + haloWidthPx - capTopOffsetEm(tier) * sizePx

// Everything derived from a label's size lives in the two functions below and nowhere else, so
// the three tiers cannot disagree about it. The per-item case is an argument here rather than
// an override at the callsite.

// A label's size is either the tier's own constant or, for the location tier, the per-item value
// the editor owns, arriving as a mapbox expression.
export type LabelSize = number | unknown[]

// `unit` converts the tier's px measurement into a multiple of the label's size, so a casing keeps
// the tier's PROPORTIONS at every size rather than its pixels — the ratio of core to counter is
// what decides whether an 'e' fills in, and it has to hold from 4px to 100px.
const sizedBy = (size: LabelSize, unit: number, factor: number): unknown => {
  const multiplier = unit * factor
  if (typeof size === 'number') return size * multiplier
  return multiplier === 1 ? size : ['*', size, multiplier]
}

// zoom has to be the OUTERMOST expression: GL rejects it anywhere else and a rejected
// expression invalidates the WHOLE LAYER, silently — it is never added and every label on it
// disappears. So the ramp takes a builder and calls it per stop; a caller needing a conditional
// gets it INSIDE each stop, the only nesting order GL accepts.
const zoomRamped = (
  isZoomScalingEnabled: boolean,
  at: (factor: number) => unknown,
): unknown =>
  isZoomScalingEnabled
    ? [
        'interpolate',
        ['linear'],
        ['zoom'],
        ZOOM_SCALE_MIN,
        at(ZOOM_SCALE_MIN_FACTOR),
        ZOOM_SCALE_MAX,
        at(1),
      ]
    : at(1)

export interface ILabelScaling {
  // Off holds a label at its tier size whatever the camera does — the map view's own setting.
  isZoomScalingEnabled?: boolean
  // Location tier only: its size is per item, set in the editor between 4px and 100px.
  size?: LabelSize
}

export interface ILabelCasing extends ILabelScaling {
  // A plated label's casing IS the plate, so the halo goes to zero under one. Pass `true` for a
  // layer that is always plated, or a boolean expression for one that decides per feature.
  // Stated here rather than in each layer because it is the same rule as the transparent halo
  // colour in labelHaloPaint, and the two have to agree.
  suppressed?: boolean | unknown[]
}

// GL wants line height and tracking in ems; the design system specifies px and %. Both are
// therefore already relative to the size and need no ramp of their own — they follow it.
export const labelTypographyLayout = (
  tier: ILabelTier,
  scaling: ILabelScaling = {},
) => ({
  'text-font': tier.font,
  'text-size': zoomRamped(!!scaling.isZoomScalingEnabled, factor =>
    sizedBy(scaling.size ?? tier.sizePx, 1, factor),
  ),
  'text-line-height': tier.lineHeightPx / tier.sizePx,
  'text-letter-spacing': tier.letterSpacingEm,
})

// The casing half of the same decision. Kept beside the typography rather than in each layer's
// paint because the core is derived from these two numbers and capped against the counters of
// the very size chosen above — split across two files they can disagree, and on the pin layer they
// did. Colour is not here: which ink and casing pair up keys on state and
// ground rather than on size.
export const labelCasingPaint = (
  tier: ILabelTier,
  casing: ILabelCasing = {},
) => {
  const size = casing.size ?? tier.sizePx
  const enabled = !!casing.isZoomScalingEnabled
  const under = (px: number): unknown => {
    if (casing.suppressed === true) return 0
    const unit = px / tier.sizePx
    // The plated case goes inside each stop, never around the ramp: zoom has to stay outermost.
    return zoomRamped(enabled, factor => {
      const value = sizedBy(size, unit, factor)
      return casing.suppressed ? ['case', casing.suppressed, 0, value] : value
    })
  }
  return {
    'text-halo-width': under(tier.haloWidthPx),
    'text-halo-blur': under(tier.haloBlurPx),
  }
}

// Icon flags mirror text flags by default: for a layer whose icon IS its artwork, the icon's
// box is the one worth reserving.

// The collision inset RIDES THE RAMP, because it is pixels. Flat, it is invisible at full size and
// enormous once a label shrinks — two flat insets then outgrow the gap between a pin and its
// own label, and the label, which yields, is evicted.
export const collisionPadding = (isZoomScalingEnabled: boolean): unknown =>
  zoomRamped(isZoomScalingEnabled, factor =>
    sizedBy(COLLISION_PADDING, 1, factor),
  )

export const labelDeclutterLayout = (
  allowOverlap: boolean,
  scaling: ILabelScaling = {},
) => {
  const padding = zoomRamped(!!scaling.isZoomScalingEnabled, factor =>
    sizedBy(COLLISION_PADDING, 1, factor),
  )
  return {
    'text-allow-overlap': allowOverlap,
    'text-ignore-placement': allowOverlap,
    'text-padding': padding,
    'icon-allow-overlap': allowOverlap,
    'icon-ignore-placement': allowOverlap,
    'icon-padding': padding,
    'symbol-sort-key': ['get', 'sortKey'] as never,
  }
}

// A label that loses to map chrome whatever the overlap setting says. Mapbox has no per-pair
// collision control, but its two flags are independent — allow-overlap gates the hit test,
// ignore-placement only picks which grid the box is filed in — and split apart they buy the
// asymmetry: yield to anything already placed, evict nothing.
export const yieldingLabelDeclutterLayout = (
  allowOverlap: boolean,
  scaling: ILabelScaling = {},
) => ({
  ...labelDeclutterLayout(allowOverlap, scaling),
  'text-allow-overlap': false,
  'icon-allow-overlap': false,
})

// The selected tier's twins: always shown, and filing their boxes, so whatever sits under a
// selected item gives way — pins and markers included, whatever the overlap setting says. A
// resting copy left under it gives way too; useReleaseWithoutFade places it back without a blink.
export const SELECTED_TIER_PLACEMENT = {
  'text-allow-overlap': true,
  'text-ignore-placement': false,
  'icon-allow-overlap': true,
  'icon-ignore-placement': false,
}

// On a label the icon is a chip fitted far taller than it paints, so it stays out of the index.
export const SELECTED_LABEL_PLACEMENT = {
  ...SELECTED_TIER_PLACEMENT,
  'icon-ignore-placement': true,
}

export const IS_SELECTED = ['get', 'selected']

// Hidden by paint rather than filtered out: the twin's box culls the copy only while overlap is
// off, and opacity is read only at draw time.
export const hiddenOpacity = (predicate: unknown): unknown => [
  'case',
  predicate,
  0,
  1,
]

export const hiddenWhen = (predicate: unknown) => {
  const opacity = hiddenOpacity(predicate)
  return { 'text-opacity': opacity, 'icon-opacity': opacity }
}

// For a label whose icon is a mostly-transparent 9-patch fitted around the text — the logo
// chip. That box is far taller than anything it paints (see CHIP_LABEL_FIT), so left in the
// collision index it reserves empty air and evicts the label from its own pin. Both overlap
// flags file it in mapbox's ignored grid instead, leaving placement to the text box.
//
// It does NOT unglue the chip: with neither half optional, mapbox resolves placeIcon and
// placeText to their conjunction, so the pair still places or drops together.
export const chipCarryingLabelDeclutterLayout = (
  allowOverlap: boolean,
  scaling: ILabelScaling = {},
) => ({
  ...yieldingLabelDeclutterLayout(allowOverlap, scaling),
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
})
